<script setup>
// SaveToMemoryButton — tiny icon next to MessageStatus in MessageMeta. Lets a
// CS agent save THIS message bubble's content to channel-scoped memory in
// Graphiti. Reuses the /api/ai-improvement/comment endpoint with kind=memory.
//
// Visibility rules:
//   - Hidden by default. Only appears on bubble hover (parent uses group/group-hover).
//   - Suppressed for AI-generated messages (they have their own Comment flow).
//   - Suppressed for private notes.
//   - Suppressed for activity messages.
//
// Channel scope defaults to 'auto' (backend infers from the conversation's
// inbox name). Agent can flip to 'all' for universal rules or pick a specific
// channel.
import { computed, ref } from 'vue';
import Icon from 'next/icon/Icon.vue';
import MemoryPopover from './MemoryPopover.vue';
import { useMessageContext } from './provider.js';
import { MESSAGE_TYPES } from './constants';

const {
  id: messageId,
  inboxId,
  content,
  contentAttributes,
  isPrivate,
  messageType,
} = useMessageContext();

const attrs = computed(() => contentAttributes?.value ?? {});
const isAi = computed(
  () =>
    attrs.value.ai_draft === true ||
    attrs.value.aiDraft === true ||
    attrs.value.ai_generated === true
);
const isActivity = computed(
  () => messageType?.value === MESSAGE_TYPES.ACTIVITY
);

// Only meaningful for incoming or outgoing customer-facing text.
const isEligible = computed(
  () =>
    !isAi.value && !isPrivate?.value && !isActivity.value && !!content?.value
);

const accountId = computed(() => {
  const m = window.location.pathname.match(/accounts\/(\d+)/);
  return m ? m[1] : '';
});
const conversationId = computed(() => {
  const m = window.location.pathname.match(/conversations\/(\d+)/);
  return m ? m[1] : '';
});
const backendUrl = computed(
  () =>
    attrs.value.backend_url || attrs.value.backendUrl || 'https://sapi.mkhb.my'
);

const open = ref(false);
const state = ref('idle'); // idle | saving | saved | error
const saved = ref(false); // sticky once a successful save lands

async function handleSubmit({ note, scope }) {
  if (state.value === 'saving') return;
  state.value = 'saving';
  try {
    const body = {
      kind: 'memory',
      conversationId: conversationId.value,
      messageId: messageId?.value,
      inboxId: inboxId?.value,
      accountId: Number(accountId.value) || undefined,
      messageContent: content?.value || '',
      comment: note || `(saved by agent — see message content above)`,
      timestamp: new Date().toISOString(),
    };
    if (scope && scope !== 'auto') {
      body.scope = scope === 'all' ? ['all'] : [scope];
    }
    const resp = await fetch(backendUrl.value + '/api/ai-improvement/comment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (resp.ok) {
      state.value = 'saved';
      saved.value = true;
      setTimeout(() => {
        open.value = false;
        state.value = 'idle';
      }, 1200);
    } else {
      state.value = 'error';
    }
  } catch {
    state.value = 'error';
  }
}
</script>

<template>
  <div v-if="isEligible" class="relative inline-flex">
    <button
      type="button"
      :title="
        saved
          ? 'Already in memory — click to add another note'
          : 'Save this to memory'
      "
      class="inline-flex items-center justify-center transition-opacity"
      :class="
        saved
          ? 'opacity-100 text-n-brand'
          : open
            ? 'opacity-100 text-n-slate-12'
            : 'opacity-0 group-hover:opacity-60 hover:!opacity-100 text-n-slate-11'
      "
      @click.stop="open = !open"
    >
      <Icon
        :icon="saved ? 'i-lucide-bookmark-check' : 'i-lucide-bookmark-plus'"
        class="size-3"
      />
    </button>
    <MemoryPopover
      v-model="open"
      title="Save to memory"
      anchor="right"
      :state="state"
      @submit="handleSubmit"
    />
  </div>
</template>
